import React, { useState } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const EligibilityForm = () => {
  const [companyName, setCompanyName] = useState("");
  const [loanAmount, setLoanAmount] = useState("");
  const [periodMonths, setPeriodMonths] = useState("");
  const [annualInterestRate, setAnnualInterestRate] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);


  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:8080";
      const response = await fetch(`${backendUrl}/api/credit/check-eligibility`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          company_name: companyName,
          loan_amount: parseFloat(loanAmount),
          period_months: parseInt(periodMonths),
          annual_interest_rate: parseFloat(annualInterestRate) / 100,
        }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || `Server error: ${response.status}`);
      }

      const data = await response.json();
      setResult(data);
    } catch (err) {
      setError(err.message || "Failed to fetch eligibility data");
    } finally {
      setLoading(false);
    }
  };

  const assessment = result?.eligibility_assessment;
  const eligibilityResult = assessment?.eligibility_assessment?.result;
  const isApproved = eligibilityResult === "APPROVED";
  const isConditional = eligibilityResult === "CONDITIONALLY_APPROVED";

  const handleExportPDF = async () => {
    const doc = new jsPDF();
    const rating = assessment?.credit_rating_analysis;
    const financial = assessment?.predicted_financial_analysis;
    const indicators = financial?.indicators || {};

    try {
      const response = await fetch(
        "https://cdn.jsdelivr.net/npm/dejavu-fonts-ttf@2.37.3/ttf/DejaVuSans.ttf"
      );
      const buffer = await response.arrayBuffer();
      const uint8 = new Uint8Array(buffer);
      let binary = "";
      for (let i = 0; i < uint8.length; i++) {
        binary += String.fromCharCode(uint8[i]);
      }
      const base64 = btoa(binary);
      doc.addFileToVFS("DejaVuSans.ttf", base64);
      doc.addFont("DejaVuSans.ttf", "DejaVuSans", "normal");
      doc.setFont("DejaVuSans");
    } catch {
      // falls back to default font — Cyrillic may not render
    }

    const fontStyle = { font: "DejaVuSans" };

    doc.setFontSize(18);
    doc.text("CreditGO Full Eligibility Report", 14, 20);

    doc.setFontSize(11);
    doc.text(`Company: ${assessment?.company_name ?? ""}`, 14, 32);
    doc.text(`Date: ${new Date().toLocaleDateString()}`, 14, 39);

    // Eligibility Assessment
    doc.setFontSize(13);
    doc.text("Eligibility Assessment", 14, 52);
    const resultValue = assessment?.eligibility_assessment?.result ?? "—";
    const resultLabel =
      resultValue === "APPROVED" ? "Approved" :
      resultValue === "CONDITIONALLY_APPROVED" ? "Conditionally Approved" :
      resultValue === "DENIED" ? "Declined" : resultValue;

    autoTable(doc, {
      startY: 56,
      head: [["Field", "Value"]],
      body: [
        ["Result", resultLabel],
        [
          "Max Recommended Loan",
          assessment?.eligibility_assessment?.max_recommended_loan != null
            ? `${assessment.eligibility_assessment.max_recommended_loan.toLocaleString(undefined, { maximumFractionDigits: 2 })} MKD`
            : "—",
        ],
      ],
      theme: "grid",
      styles: fontStyle,
      headStyles: { fillColor: [34, 197, 94], ...fontStyle },
    });

    // Credit Rating
    const afterEligibility = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(13);
    doc.text("Existing Credit Rating Analysis", 14, afterEligibility);
    autoTable(doc, {
      startY: afterEligibility + 4,
      head: [["Field", "Value"]],
      body: [
        ["Rating", rating?.rating ?? "—"],
        ["Rating Score", String(rating?.rating_score ?? "—")],
        ["Category", rating?.rating_category ?? "—"],
      ],
      theme: "grid",
      styles: fontStyle,
      headStyles: { fillColor: [34, 197, 94], ...fontStyle },
    });

    // Predicted Financial Analysis
    const afterRating = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(13);
    doc.text("Predicted Financial Analysis", 14, afterRating);

    const indicatorRows = Object.entries(indicators).map(([key, value]) => [
      key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
      typeof value === "boolean"
        ? value ? "Yes" : "No"
        : typeof value === "number"
        ? value.toLocaleString(undefined, { maximumFractionDigits: 2 })
        : String(value),
    ]);
    indicatorRows.push(
      ["DSCR", financial?.dscr?.toLocaleString() ?? "—"],
      ["DSCR Interpretation", financial?.dscr_interpretation ?? "—"]
    );

    autoTable(doc, {
      startY: afterRating + 4,
      head: [["Indicator", "Value"]],
      body: indicatorRows,
      theme: "grid",
      styles: fontStyle,
      headStyles: { fillColor: [34, 197, 94], ...fontStyle },
    });

    // Terminology
    doc.addPage();
    doc.setFontSize(13);
    doc.text("Terminology", 14, 20);

    autoTable(doc, {
      startY: 26,
      head: [["Term", "Description"]],
      body: [
        ["Result", "The result of the credit eligibility request."],
        ["Rating", "Letter grade representing the company's existing creditworthiness."],
        ["Rating Score", "Numeric score used to calculate the credit rating letter grade."],
        ["Category", "Descriptive label for the credit rating."],
        ["DSCR", "Debt Service Coverage Ratio - measures whether the company generates enough cash flow to cover its loan payments."],
        ["DSCR Interpretation", "Qualitative label for the DSCR value."],
        ["Max Recommended Loan", "The maximum loan amount the company is recommended to take on based on its financial profile."],
      ],
      theme: "grid",
      styles: { ...fontStyle, cellWidth: "wrap", fontSize: 9 },
      headStyles: { fillColor: [34, 197, 94], ...fontStyle },
      columnStyles: {
        0: { cellWidth: 50, fontStyle: "bold" },
        1: { cellWidth: "auto" },
      },
    });

    doc.save(`CreditGO-Report-Company-${assessment?.company_name ?? "report"}.pdf`);
  };

  return (
    <section id="eligibility" className="min-h-screen py-16 lg:py-24 bg-white flex items-center">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Check Your Eligibility
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Enter your company details below to instantly check your credit eligibility.
          </p>
        </div>

        {/* Form Container */}
        <div className="max-w-xl mx-auto">
          <div className="bg-background-light rounded-2xl shadow-lg p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Company Name
                </label>
                <input
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="Enter company name"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-green focus:border-transparent transition-all outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Credit Amount (MKD)
                </label>
                <input
                  type="number"
                  value={loanAmount}
                  onChange={(e) => setLoanAmount(e.target.value)}
                  placeholder="Enter loan amount in currency: Macedonian Denar"
                  required
                  min="0"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-green focus:border-transparent transition-all outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Term (months)
                </label>
                <input
                  type="number"
                  value={periodMonths}
                  onChange={(e) => setPeriodMonths(e.target.value)}
                  placeholder="Enter period of credit service"
                  required
                  min="1"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-green focus:border-transparent transition-all outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Annual Interest Rate (%)
                </label>
                <input
                  type="number"
                  value={annualInterestRate}
                  onChange={(e) => setAnnualInterestRate(e.target.value)}
                  placeholder="Enter the interest rate applied by the financial institution"
                  required
                  min="0"
                  step="0.01"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-green focus:border-transparent transition-all outline-none"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 bg-primary-green text-white font-semibold rounded-lg hover:bg-primary-green-light disabled:opacity-50 disabled:cursor-not-allowed transform hover:-translate-y-0.5 transition-all"
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Checking...
                  </span>
                ) : (
                  "Check Eligibility"
                )}
              </button>
            </form>

            {/* Error Message */}
            {error && (
              <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
                {error}
              </div>
            )}

            {/* Results Display */}
            {result && assessment && (
              <div className="mt-8 animate-fade-in-scale space-y-4">
                {/* Verdict banner */}
                <div
                  className={`p-6 rounded-xl ${
                    isApproved
                      ? "bg-green-50 border border-green-200"
                      : isConditional
                      ? "bg-yellow-50 border border-yellow-200"
                      : "bg-red-50 border border-red-200"
                  }`}
                >
                  <h3 className="text-xl font-bold flex items-center gap-2">
                    {isApproved ? (
                      <>
                        <span className="text-green-600">Approved</span>
                        <span className="text-2xl">&#10003;</span>
                      </>
                    ) : isConditional ? (
                      <>
                        <span className="text-yellow-600">Conditional Approval</span>
                        <span className="text-2xl">&#9888;</span>
                      </>
                    ) : (
                      <>
                        <span className="text-red-600">Declined</span>
                        <span className="text-2xl">&#10007;</span>
                      </>
                    )}
                  </h3>
                  <p className="mt-2 text-gray-600 text-sm">{assessment.recommendation}</p>
                </div>

                {/* Eligibility Score */}
                <div className="p-5 bg-gray-50 border border-gray-200 rounded-xl space-y-2 text-gray-700">
                  <h4 className="font-semibold text-gray-900">Eligibility Assessment</h4>
                  <p>
                    <span className="font-medium">Max Recommended Loan:</span>{" "}
                    {assessment.eligibility_assessment?.max_recommended_loan?.toLocaleString()} MKD
                  </p>
                </div>

                {/* Export button */}
                <button
                  onClick={handleExportPDF}
                  className="w-full py-3 bg-black text-white font-semibold rounded-lg hover:bg-gray-800 transform hover:-translate-y-0.5 transition-all"
                >
                  Export Full Report as PDF
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default EligibilityForm;
